import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

import logo from '/logo.svg';
import hiddenIcon from '/hiddenPassword.svg';
import unhiddenIcon from '/unhiddenPassword.svg';

import Button from 'components/Button';
import Input from 'components/Input';
import resetPasswordMutation from 'api/mutations/resetPasswordMutation';
import Loading from 'components/Loading';
import validateHashMutation from 'api/mutations/validateHashMutation';

interface IData {
  password: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  password: yup
    .string()
    .required('Please Enter your password')
    .matches(
      /^(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,}$/,
      'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character',
    ),
  confirmPassword: yup
    .string()
    .required('Please Enter your password')
    .matches(
      /^(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,}$/,
      'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character',
    ),
});

const LoginSetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mutation = resetPasswordMutation();
  const validateMutation = validateHashMutation();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState<boolean>(true);
  const [confirmHidden, setConfirmHidden] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const emailFromQuery = searchParams?.get('email') ?? '';
  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors },
  } = useForm<IData>({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (!searchParams.get('token')) {
      navigate('/auth/login-expired');
    }
  }, []);

  useEffect(() => {
    validateMutation.mutate(
      {
        hash: searchParams.get('token'),
      },
      {
        onError(error) {
          if (
            error?.message === 'This invitation is expired, request a new one'
          ) {
            navigate('/auth/login-expired');
          }
        },
      },
    );
  }, []);

  const onSubmit = (data: IData) => {
    if (data.password === data.confirmPassword) {
      mutation.mutate(
        {
          password: data.password,
          hash: searchParams.get('token'),
        },
        {
          onSuccess(res) {
            if (res.status === 204) {
              navigate('/auth/login-distributor');
            }
          },
        },
      );
    } else {
      setError(true);
    }
  };

  if (validateMutation.isPending) {
    return; 
  }

  return (
    <div className="flex flex-col w-full items-center justify-center max-h-screen">
      <div className="flex flex-col border py-[60px] px-[40px] rounded-2xl border-[#CBCBCB] w-[480px] bg-[#fff] md:w-[358px]">
        <div className="justify-center items-center flex">
          <img src={logo} alt="logo" />
        </div>
        <div className="text-3xl font-bold mt-[40px] mb-[12px] justify-center items-center flex md:text-2xl">
          {t('welcomeToEvs')}
        </div>
        <div className="text-base text-[#757575] justify-center items-center flex">
          {t('enterPasswordFor')} {emailFromQuery && t('for')}
        </div>
        <div className="text-base justify-center items-center flex mb-[40px]">
          {emailFromQuery}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <Controller
              control={control}
              rules={{
                required: true,
                minLength: 8,
              }}
              defaultValue={''}
              render={({ field: { onChange } }) => (
                <Input
                  id="email"
                  className={`w-11/12 m-4 ${errors.password && 'mb-0'}`}
                  placeholder="•••••••"
                  title={`${t('password')}*`}
                  type={hidden ? 'password' : 'text'}
                  onChange={(e) => {
                    trigger('password');
                    onChange(e);
                  }}
                  rightIcon={hidden ? hiddenIcon : unhiddenIcon}
                  onRightIconClick={() => setHidden(!hidden)}
                  arrowRightClassName="top-7 right-5"
                />
              )}
              name="password"
            />
            {errors.password && (
              <div className="text-[#FF3B30] text-xs ml-4 mt-0">
                {errors?.password?.message}
              </div>
            )}
          </div>
          <div className="relative">
            <Controller
              control={control}
              rules={{
                required: true,
                minLength: 8,
              }}
              defaultValue={''}
              render={({ field: { onChange } }) => (
                <Input
                  id="confirmPassword"
                  className="w-11/12 m-4"
                  placeholder="•••••••"
                  title={`${t('confirmPassword')}*`}
                  type={confirmHidden ? 'password' : 'text'}
                  onChange={(e) => {
                    trigger('confirmPassword');
                    onChange(e);
                  }}
                  rightIcon={confirmHidden ? hiddenIcon : unhiddenIcon}
                  onRightIconClick={() => setConfirmHidden(!confirmHidden)}
                  arrowRightClassName="top-7 right-5"
                />
              )}
              name="confirmPassword"
            />
            {error && (
              <span className="absolute bottom-[-3px] text-[#FF3B30] text-xs ml-4">
                {t('passwordDoesNotMatch')}
              </span>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              disabled={mutation.isPending}
              type="submit"
              btnType="primary"
            >
              {mutation.isPending ? <Loading size={25} /> : t('confirm')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginSetPassword;
